import React, { memo } from 'react';
import { ArrowUpRight, ArrowDownRight } from '@saleshandy/icons';
import { SkeletonLoading } from '@saleshandy/design-system';
import type { DialerReportSummaryItem } from '../../types';
import Divider from '../../../../../../../settings/components/my-profile/components/change-email-modal/change-email-modal-flow/sub-components/divider';

interface IProps {
  items: DialerReportSummaryItem[];
  loading?: boolean;
}

/**
 * Renders KPI summary cards for dialer reports (Calls Made, Connected, etc.).
 * Each card shows value, optional percentage, and trend indicator.
 */
const ReportsSummaryCards: React.FC<IProps> = memo(
  ({ items, loading = false }) => {
    if (loading) {
      return (
        <div className="dialer-reports-summary-cards d-flex gap-16 ">
          {Array.from({ length: 7 }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="dialer-reports-summary-card">
              <SkeletonLoading height={24} width={80} className="mb-2" />
              <SkeletonLoading height={16} width={120} />
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="dialer-reports-summary-cards d-flex">
        {items.map((item) => {
          const hasPositiveTrend = item?.trend != null && item?.trend >= 0;

          const trendClassName = (
            item?.key === 'callsUnanswered'
              ? !hasPositiveTrend
              : hasPositiveTrend
          )
            ? 'positive-trend'
            : 'negative-trend';

          const displayValue =
            typeof item.value === 'number'
              ? item?.value.toLocaleString()
              : String(item?.value);
          const valueWithSuffix = item?.valueSuffix
            ? `${displayValue}`
            : displayValue;

          return (
            <>
              <div key={item?.key} className="dialer-reports-summary-card">
                <div className="d-flex align-items-end flex-wrap gap-4">
                  <span className="dialer-reports-summary-card__value">
                    {valueWithSuffix}
                    <span className="dialer-reports-summary-card__value-suffix">
                      {item?.valueSuffix}
                    </span>
                  </span>
                  {item?.percentage != null && (
                    <span className="dialer-reports-summary-card__percentage">
                      (
                      {typeof item?.percentage === 'number'
                        ? item?.percentage.toFixed(1)
                        : item?.percentage}
                      %)
                    </span>
                  )}
                </div>
                <div className="dialer-reports-summary-card__label">
                  {item?.label}
                </div>

                {item?.trend != null && item?.trend !== 0 && (
                  <div
                    className={`d-flex align-items-center dialer-reports-summary-card__percentage-trend ${trendClassName}`}
                  >
                    {hasPositiveTrend ? (
                      <ArrowUpRight width={14} height={14} />
                    ) : (
                      <ArrowDownRight width={14} height={14} />
                    )}
                    <span>{Math.abs(item?.trend).toFixed(1)}%</span>
                  </div>
                )}
              </div>
              <Divider />
            </>
          );
        })}
      </div>
    );
  },
);

export default ReportsSummaryCards;
